import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xeenawel';

const tiposAula = [
  { value: 'pilates', label: 'Pilates' },
  { value: 'yoga', label: 'Yoga' },
  { value: 'hiit-burn', label: 'HIIT Burn' },
  { value: 'aconselhamento', label: 'Quero aconselhamento' }
];

const AulasDialog = ({ isOpen, onClose, defaultAula = '' }) => {
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    email: '',
    tiposAula: defaultAula ? [defaultAula] : []
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  React.useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        tiposAula: defaultAula
          ? (prev.tiposAula.includes(defaultAula) ? prev.tiposAula : [...prev.tiposAula, defaultAula])
          : prev.tiposAula
      }));
    }
  }, [isOpen, defaultAula]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nome.trim()) newErrors.nome = 'Por favor preencha este campo.';
    if (!formData.telefone.trim()) newErrors.telefone = 'Por favor preencha este campo.';
    if (!formData.email.trim()) {
      newErrors.email = 'Por favor preencha este campo.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Por favor insira um email válido.';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setSubmitting(true);
    setFeedback({ type: '', message: '' });
    const selecionados = formData.tiposAula.filter((v) => v !== 'aconselhamento');
    const labels = selecionados
      .map((v) => (tiposAula.find((t) => t.value === v) || {}).label)
      .filter(Boolean);
    const tipoAulaTexto = labels.length > 0 ? labels.join(', ') : '';
    const queroAconselhamento = formData.tiposAula.includes('aconselhamento') ? 'Sim' : 'Não';

    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: formData.nome,
          telemovel: formData.telefone,
          email: formData.email,
          tipo_aula: tipoAulaTexto,
          tipos_aula_selecionados: labels,
          quero_aconselhamento: queroAconselhamento,
          _subject: 'Nova inscrição em aula - Ultimate Fit'
        })
      });
      if (res.ok) {
        setFeedback({ type: 'success', message: 'Inscrição enviada com sucesso! Em breve entraremos em contacto.' });
        setFormData({ nome: '', telefone: '', email: '', tiposAula: [] });
        setErrors({});
      } else {
        let detail = '';
        try {
          const data = await res.json();
          if (data && Array.isArray(data.errors)) detail = data.errors.map(err => err.message).join(' ');
        } catch (_) { /* ignore */ }
        setFeedback({ type: 'error', message: detail || 'Não foi possível enviar a inscrição. Tente novamente.' });
      }
    } catch (err) {
      setFeedback({ type: 'error', message: 'Erro de ligação. Verifique a sua internet e tente novamente.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    if (feedback.message) setFeedback({ type: '', message: '' });
  };

  const toggleTipoAula = (value) => {
    setFormData((prev) => {
      const exists = prev.tiposAula.includes(value);
      const next = exists ? prev.tiposAula.filter((v) => v !== value) : [...prev.tiposAula, value];
      return { ...prev, tiposAula: next };
    });
    if (feedback.message) setFeedback({ type: '', message: '' });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="p-0 bg-white overflow-hidden flex flex-col"
        style={{
          maxHeight: '90vh',
          maxHeight: '90dvh',
          width: 'calc(100vw - 24px)',
          maxWidth: '900px'
        }}
      >
        <div className="overflow-y-auto p-5 sm:p-8 md:p-12 animate-uf-fade-in" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="mb-7 pr-8">
            <DialogTitle className="text-2xl sm:text-3xl md:text-4xl text-black mb-2" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal" }}>
              Inscreva-se nas nossas aulas
            </DialogTitle>
            <DialogDescription className="text-gray-600 text-base sm:text-lg uppercase" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", letterSpacing: "0.03em" }}>
              Escolha a modalidade ideal para si e comece a treinar 💪
            </DialogDescription>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="aulas-form">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="aulas-nome" className="text-sm font-semibold text-gray-700 mb-2 block">Nome *</Label>
                <Input
                  id="aulas-nome" name="nome" data-testid="aulas-input-nome"
                  value={formData.nome}
                  onChange={(e) => handleChange('nome', e.target.value)}
                  className={`h-12 border-2 ${errors.nome ? 'border-red-500' : 'border-gray-300'} focus:border-black`}
                  placeholder="Insira o seu nome"
                />
                {errors.nome && <p className="text-red-500 text-sm mt-1">{errors.nome}</p>}
              </div>
              <div>
                <Label htmlFor="aulas-tel" className="text-sm font-semibold text-gray-700 mb-2 block">Telemóvel *</Label>
                <Input
                  id="aulas-tel" name="telemovel" type="tel" data-testid="aulas-input-telemovel"
                  value={formData.telefone}
                  onChange={(e) => handleChange('telefone', e.target.value)}
                  className={`h-12 border-2 ${errors.telefone ? 'border-red-500' : 'border-gray-300'} focus:border-black`}
                  placeholder="Insira o seu telemóvel"
                />
                {errors.telefone && <p className="text-red-500 text-sm mt-1">{errors.telefone}</p>}
              </div>
              <div>
                <Label htmlFor="aulas-email" className="text-sm font-semibold text-gray-700 mb-2 block">Email *</Label>
                <Input
                  id="aulas-email" name="email" type="email" data-testid="aulas-input-email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className={`h-12 border-2 ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:border-black`}
                  placeholder="Insira o seu email"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-3 block">
                Tipo de aula <span className="font-normal text-gray-500">(pode selecionar mais que uma)</span>
              </Label>
              <div className="space-y-3">
                {tiposAula.map((tipo) => {
                  const checked = formData.tiposAula.includes(tipo.value);
                  return (
                    <label key={tipo.value} className="flex items-center space-x-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        name="tiposAula"
                        value={tipo.value}
                        checked={checked}
                        onChange={() => toggleTipoAula(tipo.value)}
                        data-testid={`aulas-checkbox-${tipo.value}`}
                        className="w-5 h-5 accent-yellow-400 border-2 border-gray-300 focus:ring-2 focus:ring-yellow-400 cursor-pointer"
                      />
                      <span className="text-gray-700 text-base group-hover:text-black transition-colors">{tipo.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Feedback (acima do botão para garantir visibilidade) */}
            {feedback.message && (
              <p
                role={feedback.type === 'error' ? 'alert' : 'status'}
                data-testid="aulas-feedback"
                className={`text-center text-sm mb-2 ${feedback.type === 'success' ? 'text-green-600' : 'text-red-600'}`}
                style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
              >
                {feedback.message}
              </p>
            )}

            <div className="pt-2">
              <Button
                type="submit"
                disabled={submitting}
                data-testid="aulas-submit-btn"
                className="w-full bg-yellow-400 hover:bg-yellow-500 text-black text-[20px] h-14 border-2 border-yellow-400 hover:border-yellow-500 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", letterSpacing: "-0.02em" }}
              >
                {submitting ? 'A ENVIAR...' : 'ENVIAR'}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AulasDialog;
